import { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { playCorrect, playWrong } from '../../utils/sounds';
import { createSearchBoard, getPrecisionRules, summarizeTrials, type SearchBoard } from './rules';
import type { PrecisionProps, TrialRecord } from './types';
import { PrecisionHUD, PrecisionStats, TrialStrip } from './PrecisionUI';

function Shape({ shape, tint, rotation }: { shape: number; tint: number; rotation: number }) {
  return <svg viewBox="0 0 40 40" aria-hidden="true" style={{ color: `hsl(145 19% ${tint}%)`, transform: `rotate(${rotation}deg)` }}>
    {shape === 0 ? <circle cx="20" cy="20" r="12" fill="currentColor" /> : shape === 1 ? <path d="M20 5L35 20L20 35L5 20Z" fill="currentColor" /> : shape === 2 ? <path d="M20 5L35 33H5Z" fill="currentColor" /> : <path d="M20 5L33 19H25V34H15V19H7Z" fill="currentColor" />}
  </svg>;
}

export default function PrecisionSearch({ game, mode, tier, onComplete, onFail }: PrecisionProps) {
  const rules = getPrecisionRules(game, mode, tier);
  const [round, setRound] = useState(0);
  const [board, setBoard] = useState<SearchBoard>(() => createSearchBoard(rules.gridSize, 0, rules.level));
  const [trials, setTrials] = useState<TrialRecord[]>([]);
  const [remaining, setRemaining] = useState(rules.searchWindow);
  const [resolved, setResolved] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const trialRef = useRef<TrialRecord[]>([]);
  const locked = useRef(false);
  const finished = useRef(false);
  const start = useRef(0);
  const sessionStart = useRef(performance.now());
  const nextTimer = useRef<ReturnType<typeof setTimeout>>();
  const resolveRef = useRef<(index: number | null) => void>(() => {});
  const callbacks = useRef({ onComplete, onFail });
  callbacks.current = { onComplete, onFail };
  const stats = summarizeTrials(trials, rules.rounds);
  const roundWindow = Math.max(2200, rules.searchWindow - Math.floor(round / 3) * 180);

  resolveRef.current = index => {
    if (locked.current || finished.current) return;
    locked.current = true;
    const elapsed = performance.now() - start.current;
    const timedOut = index === null || elapsed > roundWindow;
    const correct = !timedOut && index === board.targetIndex;
    const outcome = timedOut ? 'timeout' : correct ? (elapsed <= roundWindow * 0.5 ? 'perfect' : 'good') : 'wrong';
    const label = { perfect: '精准找到', good: '正确找到', wrong: '选错目标', timeout: '超过时限' }[outcome];
    const next: TrialRecord[] = [...trialRef.current, { index: round, outcome, responseMs: index === null ? null : Math.round(elapsed), label }];
    trialRef.current = next;
    setTrials(next);
    setResolved(true);
    setSelected(index);
    setFeedback(label);
    if (correct) playCorrect(); else playWrong();
    const summary = summarizeTrials(next, rules.rounds);
    const failed = summary.mistakes >= rules.mistakeLimit;
    nextTimer.current = setTimeout(() => {
      if (failed || next.length >= rules.rounds) {
        finished.current = true;
        const precision = { ...summary, version: 1 as const, tier: rules.level, trials: next, durationSeconds: (performance.now() - sessionStart.current) / 1000,
          failureReason: failed ? `失误达到 ${rules.mistakeLimit} 次。先阅读本轮条件，再逐行扫描；亮度与形状可能同时干扰。` : undefined };
        const replay = { mechanic: 'search', precision };
        if (failed) callbacks.current.onFail(replay); else callbacks.current.onComplete(summary.score, replay);
      } else {
        setBoard(createSearchBoard(rules.gridSize, round + 1, rules.level));
        setRound(round + 1);
        setSelected(null);
        setResolved(false);
      }
    }, 680);
  };

  // All cells appear together; the clock never runs during staggered reveal animations.
  useLayoutEffect(() => {
    finished.current = false;
    locked.current = false;
    start.current = performance.now();
    setRemaining(roundWindow);
    const timer = setTimeout(() => resolveRef.current(null), roundWindow);
    const interval = setInterval(() => {
      if (!locked.current) setRemaining(Math.max(0, roundWindow - (performance.now() - start.current)));
    }, 50);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, [round, roundWindow]);
  useEffect(() => () => { finished.current = true; clearTimeout(nextTimer.current); }, []);

  return <div className="precision-game">
    <PrecisionHUD round={round + 1} total={rules.rounds} combo={stats.combo} mistakes={stats.mistakes} limit={rules.mistakeLimit} progress={remaining / roundWindow} label={board.ruleKind} />
    <div className="precision-rule"><span>本轮指令</span><strong>{board.rule}</strong><small>{rules.gridSize} × {rules.gridSize} 搜索阵列 · 剩余 {(remaining / 1000).toFixed(1)} 秒</small></div>
    <div className="precision-grid" style={{ gridTemplateColumns: `repeat(${rules.gridSize}, minmax(0, 1fr))` }}>
      {board.items.map((item, index) => <button key={`${round}-${index}`} type="button" aria-label={`第${Math.floor(index / rules.gridSize) + 1}行第${index % rules.gridSize + 1}列`} disabled={resolved}
        className={`precision-cell ${resolved && index === board.targetIndex ? 'correct' : resolved && selected === index ? 'incorrect' : ''}`}
        onPointerDown={e => { if (!e.isPrimary || e.button !== 0) return; e.preventDefault(); resolveRef.current(index); }}
        onClick={e => { if (e.detail === 0) resolveRef.current(index); }}>
        <Shape {...item} />
        {resolved && index === board.targetIndex && <Check size={13} className="precision-cell-mark" />}
        {resolved && index === selected && index !== board.targetIndex && <X size={13} className="precision-cell-mark" />}
      </button>)}
    </div>
    <div className="precision-feedback" role="status">{resolved ? `${feedback}${selected !== board.targetIndex ? ' · 描边为正确目标' : ''}` : round >= 3 ? '节奏逐步收紧，先辨认再选择' : '每轮只有一个正确答案'}</div>
    <TrialStrip trials={trials} total={rules.rounds} />
    <PrecisionStats stats={[{ label: '本局评分', value: stats.score }, { label: '准确率', value: trials.length ? `${stats.accuracy}%` : '—' }, { label: '精准找到', value: stats.perfectCount }]} />
    <p className="precision-note">限时前半程答对记为精准。错误与超时都会中断连击，不可连续点击试答案。</p>
  </div>;
}
